import { useState } from "react";
import { Wrench } from "lucide-react";
import { api } from "../lib/api.js";

/**
 * Pinned banner while the active team is an unclaimed preloaded brand — Admin
 * Mode (docs/plans/preload-brand-studio.md). Everything in the app works on the
 * brand as it would for a team; the banner says whose it is, what happens to
 * the work when its first signup claims it, and gets the admin back out.
 * Leaving drops the membership and hard-navigates to the console, re-scoping
 * every cached query (as ImpersonationBanner does).
 */
export function AdminModeBanner({
  brandName,
  domain,
}: {
  brandName: string;
  domain: string | null;
}) {
  const [busy, setBusy] = useState(false);

  async function leave() {
    setBusy(true);
    try {
      if (domain) await api.adminPreloadLeave(domain);
    } catch {
      /* the console is still the right place to land */
    }
    window.location.href = "/admin";
  }

  return (
    <div
      data-testid="admin-mode-banner"
      className="relative z-30 flex items-center justify-center gap-3 flex-wrap px-4 py-2 text-[13px] bg-[var(--color-mx-electric)]/12 border-b border-[var(--color-mx-electric)]/30 text-[var(--color-mx-electric)]"
    >
      <span className="inline-flex items-center gap-2">
        <Wrench className="h-3.5 w-3.5" aria-hidden="true" />
        Admin mode · <span className="font-medium">{brandName}</span>
        <span className="text-[var(--color-mx-electric)]/70 hidden sm:inline">
          {domain ? `· handed to the first signup from ${domain}, decks kept, chat removed` : ""}
        </span>
      </span>
      <button
        type="button"
        onClick={leave}
        disabled={busy}
        data-testid="leave-admin-mode"
        className="underline underline-offset-2 hover:no-underline disabled:opacity-60"
      >
        {busy ? "Leaving…" : "Leave admin mode"}
      </button>
    </div>
  );
}
